import { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiSectionHeader,
  PriceRollup,
} from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  ICONS_WEB,
  OPTION_STATUS,
} from "@miplatform/constants";
import {
  filterMaterialsByAttributes,
  simpleSort,
  sortByOptionStatus,
} from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { SearchResultsContainer } from "@mibuildweb/components";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { CollapsibleIcon } from "../../../../components/CollapsibleIcon/CollapsibleIcon";
import { MaterialCard } from "../../../../components/MaterialCard/MaterialCard";
import { ItemTemplatePlanArea } from "../ItemTemplatePlanArea/ItemTemplatePlanArea";
import { MaterialDrawer } from "../MaterialDrawer/MaterialDrawer";
import { PlanAreaOption } from "../PlanAreaOption/PlanAreaOption";
import { AreaDrawer } from "../SpecConfigForm/AreaDrawer/AreaDrawer";
import { useSpecAreaFormViewModel } from "../SpecConfigForm/useSpecAreaFormViewModel";
import { useSpecMaterialFormViewModel } from "../SpecConfigForm/useSpecMaterialFormViewModel";
import { OptionsSection } from "./OptionsSection/OptionsSection";
import { useSpecOptionsFormViewModel } from "./useSpecOptionsFormViewModel";

export function SpecOptionsForm({
  stateManager,
  activeStep,
  planDetails,
  priceRollup,
  setDisableNextStep,
}) {
  const {
    showAreaDrawer,
    areaData,
    planFloors,
    availableFloors,
    collapsedFloors,
    selectedFloor,
    selectedAreaId,
    setAreaData,
    setPlanFloors,
    getFloors,
    setSelectedAreaId,
    setSelectedFloor,
    handleOpenAddAreaDrawer,
    handleCloseAreaDrawer,
    handleCollapseFloor,
  } = useSpecAreaFormViewModel({
    stateManager,
  });

  const {
    selectedItems,
    checkedAreas,
    materialOptions,
    categoryFilterOptions,
    selectedCategory,
    selectedAttributeValues,
    search,
    showCategoryFilter,
    debouncedSearch,
    debouncedItemTemplateSearch,
    hasFilters,
    materialResults,
    isLoading,
    noResultsHelperText,
    openMaterialResults,
    itemTemplatesResult,
    selectedItemTemplate,
    setSelectedItemTemplate,
    itemTemplateSearch,
    templateFilter,
    seriesOptions,
    filteredAreaTypeOptions,
    openItemTemplateResults,
    getSeriesOptions,
    getAreaTypeOptions,
    getItemTemplates,
    handleAreaTypeOptionFilter,
    handleSeriesOptionFilter,
    setOpenItemTemplateResults,
    resetMaterialDrawerStates,
    setOpenMaterialResults,
    setShowCategoryFilter,
    setSelectedItems,
    handleCloseItemTemplateResults,
    setFilteredMaterialOptions,
    setCheckedAreas,
    getCategoryFilters,
    getMaterialOptions,
    handleResetMaterialOptions,
    handleResetAddItems,
    handleCategoryClick,
    handleAttributeValueClick,
    handleItemTemplateSearch,
    handleItemSearch,
  } = useSpecMaterialFormViewModel({
    stateManager,
    planDetails,
  });

  const {
    optionDetailsMap,
    setOptionDetailsMap,
    getOption,
    optionData,
    setOptionData,
    selectedOption,
    setSelectedOption,
    previewOptionData,
    getPreviewCompatibility,
    optionPriceSummaryMap,
    getAllOptionPriceSummaries,
    mapModifiedPlanAreasAndMaterials,
    handleCreateOption,
    handleUpdateOption,
    handleDeleteOption,
    handleAddAreaToOption,
    handleRemoveAreaFromOption,
    handleUndoRemoveAreaFromOption,
    handleAddItemToArea,
    handleAddItemTemplateToArea,
    handleRemoveItemFromArea,
    handleRemoveAllItemsFromArea,
    handleReturnItemToPlan,
    handleReturnAllItemsToPlan,
    mergeOptionAndPlanAreas,
  } = useSpecOptionsFormViewModel({
    stateManager,
    planDetails,
  });

  const [showOptionPreviews, setShowOptionPreviews] = useState(false);
  const [showOptionSummary, setShowOptionSummary] = useState(false);

  const { classes } = useStyles({ openMaterialResults });
  const theme = useTheme();

  const sortedPlanAreas = planDetails.planAreas?.sort(
    (a, b) => a.displayOrder - b.displayOrder
  );

  const groupAreasByFloor = (areas) => {
    return areas.length ? Object.groupBy(areas, (area) => area.floorId) : {};
  };

  const mergedPlanAndOptionAreas = useMemo(() => {
    const planAreas = planDetails?.planAreas;
    const optionPlanAreas = optionData?.planAreas;
    const mergedAreas = mergeOptionAndPlanAreas(
      optionPlanAreas || [],
      planAreas || []
    );
    return mergedAreas;
  }, [optionData?.planAreas, planDetails?.planAreas, selectedOption]);

  const mergedPlanAndPreviewAreas = useMemo(() => {
    const previewAreas = [];
    previewOptionData.forEach((option) => {
      previewAreas.push(...option.planAreas);
    });
    return mergeOptionAndPlanAreas(
      previewAreas || [],
      planDetails?.planAreas || []
    );
  }, [JSON.stringify(previewOptionData), planDetails?.planAreas]);

  const groupedMergedAreas = useMemo(
    () =>
      Object.groupBy(
        (!showOptionPreviews
          ? mergedPlanAndOptionAreas
          : mergedPlanAndPreviewAreas) ?? [],
        (area) => area.floorId
      ),
    [mergedPlanAndOptionAreas, mergedPlanAndPreviewAreas, showOptionPreviews]
  );

  const isEditingOption = !!optionData?.id;

  useEffect(() => {
    if (availableFloors?.length || sortedPlanAreas?.length > 0) {
      // derive saved floors from plan areas
      const planAreaFloors = availableFloors?.length
        ? Object.keys(groupAreasByFloor(sortedPlanAreas))?.map((key) =>
            availableFloors?.find((floor) => {
              return floor?.id === key;
            })
          )
        : [];
      const sortedPlanAreaFloors = planAreaFloors.sort(
        (a, b) => a.displayOrder - b.displayOrder
      );
      setPlanFloors(sortedPlanAreaFloors);
    }
  }, [availableFloors?.length]);

  useEffect(() => {
    handleResetAddItems();
    setOpenMaterialResults(false);
    getCategoryFilters();
    getSeriesOptions();
    getAreaTypeOptions();
    getFloors();
  }, [activeStep]);

  useEffect(() => {
    if (!isEditingOption) {
      setOptionData({});
      handleCloseAreaDrawer();
      handleCloseCategoryFilter();
    }
    setDisableNextStep(isEditingOption);
  }, [isEditingOption]);

  useEffect(() => {
    if (
      (debouncedSearch || selectedCategory?.categoryId) &&
      showCategoryFilter
    ) {
      getMaterialOptions();
    }
    if ((!debouncedSearch || !selectedCategory?.categoryId) && !isLoading) {
      handleResetMaterialOptions();
    }
  }, [debouncedSearch, selectedCategory]);

  useEffect(() => {
    if (
      selectedCategory?.categoryId &&
      Object.keys(selectedAttributeValues).length > 0
    ) {
      // filter displayed material options by attribute value(s)
      setFilteredMaterialOptions(
        filterMaterialsByAttributes(materialOptions, selectedAttributeValues)
      );
    } else {
      // reset filtered material options to results from selected category and/or search term
      setFilteredMaterialOptions(materialOptions);
    }
  }, [materialOptions, selectedAttributeValues]);

  useEffect(() => {
    if (selectedAreaId && checkedAreas.length === 0) {
      const selectedArea = mergedPlanAndOptionAreas.find(
        (area) => area.id === selectedAreaId
      );
      if (selectedArea) {
        setCheckedAreas([selectedArea]);
      }
    }
  }, [checkedAreas.length]);

  useEffect(() => {
    if (planDetails?.options?.length > 0) {
      // Generate a map object keyed by each option's id
      const initialOptionsMap = planDetails.options
        .sort((a, b) => simpleSort(a.name, b.name))
        .reduce((map, option) => {
          return {
            ...map,
            [option.id]: {
              ...option,
              showPreview: false,
              ...mapModifiedPlanAreasAndMaterials(option.planAreas ?? []),
            },
          };
        }, {});

      setOptionDetailsMap(initialOptionsMap);
    }
  }, [planDetails]);

  useEffect(() => {
    if (!showOptionPreviews) {
      setOptionDetailsMap((prev) => {
        const updatedOptions = { ...prev };
        Object.keys(updatedOptions).forEach((key) => {
          updatedOptions[key].showPreview = false;
        });
        return updatedOptions;
      });
    }
  }, [showOptionPreviews]);

  useEffect(() => {
    getItemTemplates();
  }, [templateFilter, debouncedItemTemplateSearch]);

  const handleOpenCategoryFilter = useCallback((area, floor) => {
    setSelectedAreaId(area.id);
    setCheckedAreas([area]);
    setSelectedFloor(floor);
    setShowCategoryFilter(true);
  }, []);

  const handleCloseCategoryFilter = useCallback(() => {
    setSelectedAreaId(null);
    setCheckedAreas([]);
    setShowCategoryFilter(false);
    resetMaterialDrawerStates();
  }, []);

  const renderPlanAndOptionAreas = (mergedAreas, floor) => {
    return mergedAreas.map((area) => {
      return (
        <PlanAreaOption
          key={area.id}
          area={area}
          isLoading={isLoading}
          stateManager={stateManager}
          isEditing={isEditingOption}
          selectedAreaId={selectedAreaId}
          showRemovedMaterials
          showOptionSummary={showOptionSummary}
          showAreaDrawer={showAreaDrawer}
          showCategoryFilter={showCategoryFilter}
          handleOpenCategoryFilter={() => handleOpenCategoryFilter(area, floor)}
          handleRemoveAreaFromOption={() =>
            handleRemoveAreaFromOption(area, floor)
          }
          handleRemoveItemFromArea={handleRemoveItemFromArea}
          handleRemoveAllItemsFromArea={() =>
            handleRemoveAllItemsFromArea(area)
          }
          handleUndoRemoveAreaFromOption={() =>
            handleUndoRemoveAreaFromOption(area)
          }
          handleReturnItemToPlan={handleReturnItemToPlan}
          handleReturnAllItemsToPlan={() => handleReturnAllItemsToPlan(area)}
        />
      );
    });
  };

  return (
    <Box className={classes.root}>
      <MiBox className={classes.container} height="auto">
        <MiSectionHeader
          title="Add Options"
          subtitle="Select areas and items from the bid to remove, or add areas and items from the library to modify the option."
          rightAction={
            <PriceRollup
              data={priceRollup}
              optionDetailsMap={optionDetailsMap}
              optionPriceSummaryMap={optionPriceSummaryMap}
            />
          }
        />
        <Box className={classes.flexContainer}>
          <Box className={classes.overflowContainer}>
            {planFloors?.length > 0 &&
              planFloors.map((floor) => {
                const isCollapsed = collapsedFloors[floor?.id] ?? false;
                const mergedAreas =
                  sortByOptionStatus(groupedMergedAreas[floor?.id]) ?? [];
                const displayedAreasLength = mergedAreas.filter((area) => {
                  return area.optionStatus !== OPTION_STATUS.REMOVED;
                }).length;

                return (
                  <Accordion
                    key={floor}
                    className={classes.floorCardContainer}
                    expanded={!isCollapsed}
                    disableGutters
                    style={{
                      border:
                        selectedFloor?.id === floor?.id && showAreaDrawer
                          ? `2px solid ${theme.palette.orange.main}`
                          : "2px solid transparent",
                    }}
                  >
                    <AccordionSummary>
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        className={classes.accordionSummaryBox}
                      >
                        <Box>
                          {floor?.name ? (
                            <Typography
                              fontWeight={theme.fontWeight.bold}
                              paddingLeft={theme.spacing(2)}
                            >
                              {`${floor?.name} - ${floor?.displayOrder}`}
                            </Typography>
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "2rem" }}
                              width={200}
                            />
                          )}
                        </Box>
                        <Box className={classes.floorCardHeaderContainer}>
                          {isEditingOption && (
                            <MiButton
                              title="Add Area"
                              variant={BUTTON_VARIANTS.TEXT}
                              startIcon={{
                                path: mdi[ICONS_WEB.PLUS],
                                iconSize: 0.8,
                              }}
                              onClick={() => handleOpenAddAreaDrawer(floor)}
                              disabled={
                                showCategoryFilter ||
                                isLoading ||
                                showOptionSummary
                              }
                            />
                          )}
                          <Typography variant="body1">
                            Areas: {displayedAreasLength}
                          </Typography>
                          <CollapsibleIcon
                            isCollapsed={!isCollapsed}
                            onCollapse={() => handleCollapseFloor(floor?.id)}
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails className={classes.areaColumnsContainer}>
                      <Box className={classes.areaColumnContainer}>
                        {renderPlanAndOptionAreas(mergedAreas, floor)}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
          </Box>
          {!openMaterialResults && !openItemTemplateResults && (
            <OptionsSection
              optionDetailsMap={optionDetailsMap}
              setOptionDetailsMap={setOptionDetailsMap}
              getOption={getOption}
              stateManager={stateManager}
              priceRollup={priceRollup}
              optionData={optionData}
              setOptionData={setOptionData}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              showOptionSummary={showOptionSummary}
              setShowOptionSummary={setShowOptionSummary}
              showOptionPreviews={showOptionPreviews}
              setShowOptionPreviews={setShowOptionPreviews}
              getPreviewCompatibility={getPreviewCompatibility}
              optionPriceSummaryMap={optionPriceSummaryMap}
              getAllOptionPriceSummaries={getAllOptionPriceSummaries}
              handleCreateOption={handleCreateOption}
              handleUpdateOption={handleUpdateOption}
              handleDeleteOption={handleDeleteOption}
              handleCloseAreaDrawer={handleCloseAreaDrawer}
              handleCloseCategoryFilter={handleCloseCategoryFilter}
            />
          )}
        </Box>
      </MiBox>
      <AreaDrawer
        title="Empty Areas"
        rightIcon={mdi[ICONS_WEB.CLOSE]}
        open={showAreaDrawer}
        onClose={handleCloseAreaDrawer}
        showTabs={false}
        planAreas={sortedPlanAreas ?? []}
        areaData={areaData}
        setAreaData={setAreaData}
        selectedFloor={selectedFloor}
        planDetails={planDetails}
        actions={{
          emptyAreas: [
            {
              variant: BUTTON_VARIANTS.CONTAINED,
              name: `Add Areas to Floor ${selectedFloor?.name}`,
              disabled: !areaData.length,
              onClick: () => {
                handleAddAreaToOption(areaData, selectedFloor);
                handleCloseAreaDrawer();
              },
              startIcon: { path: mdi[ICONS_WEB.PLUS] },
            },
          ],
        }}
      />
      <SearchResultsContainer
        open={openMaterialResults}
        onClose={() => {
          handleResetAddItems();
          setOpenMaterialResults(false);
        }}
        alertTitle="Select Item and add to Area."
        alertDescription="Select an Item and use the Add to Area button to add the Item to the highlighted Area."
        storageKey="planOptions-alert"
        isLoading={isLoading}
        results={materialResults}
        noResultsHelperText={noResultsHelperText}
        hasFilters={hasFilters}
        handleResetFilterSelection={handleResetAddItems}
        renderResults={() =>
          materialResults.map((material) => {
            const isSelected = selectedItems?.id === material.id;
            return (
              <MaterialCard
                key={material.id}
                item={material}
                isSelected={isSelected}
                onClickCard={() => {
                  if (!isSelected) {
                    setSelectedItems(material);
                  }
                }}
                rightAction={
                  isSelected && (
                    <MiButton
                      title="Add to Area"
                      variant={BUTTON_VARIANTS.CONTAINED}
                      disabled={!checkedAreas.length}
                      onClick={() =>
                        handleAddItemToArea([...checkedAreas], selectedItems)
                      }
                    />
                  )
                }
              />
            );
          })
        }
      />
      <SearchResultsContainer
        open={openItemTemplateResults}
        onClose={handleCloseItemTemplateResults}
        alertTitle="Select Item Template(s) and add to a area."
        alertDescription="After selecting the item(s), choose the Area and select the Add Selected Items button."
        storageKey="itemTemplatesOptions-alert"
        isLoading={isLoading}
        results={itemTemplatesResult}
        hasFilters={hasFilters}
        renderResults={() =>
          itemTemplatesResult.map((template) => (
            <ItemTemplatePlanArea
              key={template.planAreaId}
              itemTemplate={template}
              checkedAreas={checkedAreas}
              setSelectedItemTemplate={setSelectedItemTemplate}
              selectedItemTemplate={selectedItemTemplate}
              openItemTemplateResults={openItemTemplateResults}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              handleAddItemTemplatesToAreas={() => {
                handleAddItemTemplateToArea([...checkedAreas], selectedItems);
              }}
            />
          ))
        }
      />
      <MaterialDrawer
        open={showCategoryFilter}
        onClose={handleCloseCategoryFilter}
        planDetails={planDetails}
        resetMaterialDrawerStates={resetMaterialDrawerStates}
        // Category filter props
        search={search}
        handleItemSearch={handleItemSearch}
        categoryFilterOptions={categoryFilterOptions}
        selectedCategory={selectedCategory}
        selectedAttributeValues={selectedAttributeValues}
        handleCategoryClick={handleCategoryClick}
        handleAttributeValueClick={handleAttributeValueClick}
        // item template filter props
        itemTemplateSearch={itemTemplateSearch}
        handleItemTemplateSearch={handleItemTemplateSearch}
        templateFilter={templateFilter}
        seriesOptions={seriesOptions}
        filteredAreaTypeOptions={filteredAreaTypeOptions}
        handleSeriesOptionFilter={handleSeriesOptionFilter}
        handleAreaTypeOptionFilter={handleAreaTypeOptionFilter}
        setOpenItemTemplateResults={setOpenItemTemplateResults}
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    overflow: "auto",
    height: "100%",
  },
  container: {
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  floorCardContainer: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    "&::before": {
      content: "none",
    },
    width: "100%",
  },
  floorCardHeaderContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  areaColumnContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  areaColumnsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  overflowContainer: {
    overflow: "auto",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    "& .MuiAccordion-root": {
      borderRadius: 8,
    },
    "& .MuiAccordionSummary-root:hover": {
      cursor: "default",
    },
  },
  flexContainer: {
    display: "flex",
    gap: 8,
    height: "100%",
    overflow: "auto",
    padding: theme.spacing(0.5),
    marginRight: theme.spacing(2),
  },
  accordionSummaryBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
}));
